import { useCallback, useEffect, useState } from 'react'
import { listBookings, listResources, PAGE_MAX } from '@/lib/api'
import { bookingsToCalendarProps, localDayBoundsUtc, todayLocalDate } from '@/lib/calendar'
import type { CalendarProps } from '@/lib/render'

interface TodayCalendarState {
  loading: boolean
  error: string | null
  props: CalendarProps | null
}

// Fetches today's bookings (in the tenant timezone) + resources directly from the
// API and assembles the day-view CalendarProps — no AI round-trip. Used to seed the
// operator chat with a "today's schedule" message. Pass enabled=false (e.g. for
// customers) to skip fetching entirely.
export function useTodayCalendar(timezone: string | undefined, enabled = true) {
  const tz = timezone ?? 'UTC'
  const [state, setState] = useState<TodayCalendarState>({ loading: enabled, error: null, props: null })

  const load = useCallback(async () => {
    if (!enabled) {
      setState({ loading: false, error: null, props: null })
      return
    }
    setState(s => ({ ...s, loading: true, error: null }))
    try {
      const localDate = todayLocalDate(tz)
      const { from, to } = localDayBoundsUtc(localDate, tz)
      // One day never exceeds a page, but the cap is real — read it explicitly
      // rather than relying on an unbounded response.
      const [bookingsRes, resourcesRes] = await Promise.all([
        listBookings({ from, to, limit: PAGE_MAX }),
        // Retired machines included on purpose: a booking already on one must keep
        // its label. bookingsToCalendarProps drops the column unless it is in use.
        listResources({ limit: PAGE_MAX, includeInactive: true }),
      ])
      const props = bookingsToCalendarProps(bookingsRes.items, resourcesRes.items, localDate, tz)
      setState({ loading: false, error: null, props })
    } catch (e) {
      setState({
        loading: false,
        error: e instanceof Error ? e.message : 'Failed to load calendar',
        props: null,
      })
    }
  }, [tz, enabled])

  useEffect(() => {
    load()
  }, [load])

  return { ...state, refresh: load }
}
